import { formatDollar, formatKstTime, formatNumber, formatSignedUsdc, visibleSide } from "./format";
import type { LedgerSummaryResponse, PortfolioResponse } from "./types";

export interface PositionCard {
  symbol: string;
  direction: "long" | "short" | "flat";
  imageUrl: string;
  sizeLabel: string;
  entryLabel: string;
  valueLabel: string;
  pnlLabel: string;
  pnlTone: "gain" | "loss" | "flat";
  roeLabel: string;
  leverageLabel: string;
  marginLabel: string;
  liquidationLabel?: string;
  openedAtLabel: string;
  elapsedLabel: string;
  /** Normalized entry thesis from the opening fill (full text; UI wraps, no ellipsis). */
  entryReason?: string;
}

export type TradeRowKind =
  | "entry"
  | "exit"
  | "exit_reduce"
  | "exit_breakeven"
  | "fee_only"
  | "status";
export type TradeBadgeTone = "long" | "short" | "close" | "fee" | "neutral" | "error";

export interface TradeRow {
  id: string;
  noteId?: string;
  symbol: string;
  imageUrl: string;
  kind: TradeRowKind;
  badgeLabel: string;
  badgeTone: TradeBadgeTone;
  sideLabel: string;
  sizeLabel: string;
  priceLabel: string;
  pnlLabel: string;
  pnlTone: "gain" | "loss" | "flat";
  feeLabel?: string;
  netPnlLabel?: string;
  roundTripNetLabel?: string;
  sideMark?: SideMark;
  timeLabel: string;
}

export type TradeTapeFilter = "trades" | "all" | "fees" | "errors";

/** Hyperliquid-style corner mark on coin avatars. */
export type SideMark = "long" | "short";

export function sideMarkLabel(mark: SideMark) {
  return mark === "long" ? "L" : "S";
}

export function sideMarkFromDirection(direction: PositionCard["direction"]): SideMark | undefined {
  if (direction === "long" || direction === "short") return direction;
  return undefined;
}

export function sideMarkFromSize(size: string | number | undefined): SideMark | undefined {
  const n = Number(size);
  if (!Number.isFinite(n) || n === 0) return undefined;
  return n > 0 ? "long" : "short";
}

function sideMarkFromExecution(
  execution: LedgerSummaryResponse["recentExecutions"][number],
  kind: TradeRowKind,
  badgeTone: TradeBadgeTone,
): SideMark | undefined {
  if (kind === "status") return undefined;
  if (kind === "exit" || kind === "exit_reduce" || kind === "exit_breakeven") {
    if (execution.side === "sell") return "long";
    if (execution.side === "buy") return "short";
    return undefined;
  }
  if (badgeTone === "long") return "long";
  if (badgeTone === "short") return "short";
  if (execution.side === "buy") return "long";
  if (execution.side === "sell") return "short";
  return undefined;
}

export function tokenImageUrl(symbol: string) {
  const normalized = symbol.trim().toUpperCase();
  if (!/^[A-Z0-9]{2,16}$/.test(normalized)) return "";
  return `https://app.hyperliquid.xyz/coins/${encodeURIComponent(normalized)}.svg`;
}

const INTENT_WORD_IGNORE = new Set([
  "ava",
  "mason",
  "ethan",
  "logan",
  "noah",
  "carter",
  "scout",
  "close",
  "entry",
  "execution",
  "exit",
  "delegate",
  "stop",
  "thesis",
  "fail",
  "filled",
  "intent",
  "trade",
  "market",
  "collab",
  "limit",
  "buy",
  "sell",
  "long",
  "short",
  "v2",
]);

function inferredSymbol(intentId: string, explicit?: string) {
  if (explicit?.trim()) return explicit.trim().toUpperCase();
  const lowerIntent = intentId.toLowerCase();
  const token = lowerIntent
    .split(/[-_:]+/)
    .find(
      (part) =>
        /^[a-z][a-z0-9]{1,11}$/.test(part) &&
        !INTENT_WORD_IGNORE.has(part) &&
        !/^[a-f0-9]{6,}$/.test(part),
    );
  return token?.toUpperCase() ?? "-";
}

function timestampFromIntentId(intentId: string) {
  const match = intentId.match(/\b(20\d{6})(?:[Tt]?(\d{4})(?:\d{2})?Z?|[-_](\d{4}))\b/);
  if (!match) return undefined;
  const [, datePart, compactTimePart, separatedTimePart] = match;
  const timePart = compactTimePart ?? separatedTimePart;
  if (!timePart) return undefined;
  const year = Number(datePart.slice(0, 4));
  const month = Number(datePart.slice(4, 6));
  const day = Number(datePart.slice(6, 8));
  const hour = Number(timePart.slice(0, 2));
  const minute = Number(timePart.slice(2, 4));
  const timestamp = Date.UTC(year, month - 1, day, hour, minute);
  return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : undefined;
}

function timeLabel(value?: string) {
  return formatKstTime(value);
}

function elapsedLabel(openedAt: string | undefined, now: Date) {
  if (!openedAt) return "경과 확인 중";
  const start = new Date(openedAt).getTime();
  if (!Number.isFinite(start)) return "경과 확인 중";
  const elapsedMinutes = Math.max(0, Math.floor((now.getTime() - start) / 60_000));
  if (elapsedMinutes < 60) return `${elapsedMinutes}분 경과`;
  const hours = Math.floor(elapsedMinutes / 60);
  const minutes = elapsedMinutes % 60;
  if (hours < 24) return minutes ? `${hours}시간 ${minutes}분 경과` : `${hours}시간 경과`;
  const days = Math.floor(hours / 24);
  return `${days}일 ${hours % 24}시간 경과`;
}

function toneFor(value: string | number | undefined): "gain" | "loss" | "flat" {
  const number = Number(value);
  if (!Number.isFinite(number) || number === 0) return "flat";
  return number > 0 ? "gain" : "loss";
}

function percentLabel(value?: string) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  const sign = number > 0 ? "+" : "";
  return `${sign}${(number * 100).toFixed(2)}%`;
}

/** Latest filled non-reduceOnly execution for this symbol (opening leg). */
export function matchOpenEntryExecution(symbol: string, ledger?: LedgerSummaryResponse) {
  return (ledger?.recentExecutions ?? []).find((execution) => {
    if (!executionHasFillEvidence(execution)) return false;
    if (execution.reduceOnly === true) return false;
    return inferredSymbol(execution.intentId, execution.symbol) === symbol;
  });
}

export function portfolioSizeBySymbol(portfolio: PortfolioResponse | undefined) {
  const sizes = new Map<string, number>();
  for (const entry of portfolio?.userState?.assetPositions ?? []) {
    const position = entry.position;
    const symbol = position?.coin?.toUpperCase();
    const size = Number(position?.szi);
    if (!symbol || !Number.isFinite(size) || size === 0) continue;
    sizes.set(symbol, size);
  }
  return sizes;
}

export function formatPositionEntryReason(thesis?: string | null): string | undefined {
  const normalized = thesis?.replace(/\s+/g, " ").trim();
  return normalized || undefined;
}

function executionObservedAt(
  execution: LedgerSummaryResponse["recentExecutions"][number] | undefined,
  ledger?: LedgerSummaryResponse,
) {
  if (!execution) return undefined;
  return (
    execution.reportedAt ??
    execution.lastOpenOrderSeenAt ??
    timestampFromIntentId(execution.intentId) ??
    ledger?.observedAt
  );
}

function nonFillStatusLabel(status: string) {
  if (status === "submitted_no_fill_no_open_order") return "미체결 종료";
  if (status === "submitted") return "제출됨";
  if (["exchange_error", "error", "rejected"].includes(status)) return "거부/확인 필요";
  if (status === "simulated") return "모의 실행";
  return "확인 필요";
}

function entryDirectionLabel(side?: string) {
  if (side === "buy") return "롱 진입";
  if (side === "sell") return "숏 진입";
  return "진입";
}

function exitPnlPresentation(closedPnlNum: number, feesNum: number) {
  if (closedPnlNum !== 0) {
    return {
      pnlLabel: formatSignedUsdc(closedPnlNum),
      pnlTone: toneFor(closedPnlNum),
    };
  }
  if (feesNum > 0) {
    return {
      pnlLabel: "본전",
      pnlTone: "flat" as const,
    };
  }
  return {
    pnlLabel: "청산",
    pnlTone: "flat" as const,
  };
}

export function executionHasFillEvidence(
  execution: LedgerSummaryResponse["recentExecutions"][number],
): boolean {
  const status = String(execution.status ?? "");
  if (status === "filled" && (execution.fillCount ?? 0) > 0) return true;
  if (status === "filled_unmatched_ws" && execution.fillResult) return true;
  return false;
}

function classifyTradeRow(
  execution: LedgerSummaryResponse["recentExecutions"][number],
  isRealFill: boolean,
  closedPnlNum: number,
  feesNum: number,
  portfolioPositions?: Map<string, number>,
  runnerRemainedByExitId?: Map<string, boolean>,
): Pick<
  TradeRow,
  | "kind"
  | "badgeLabel"
  | "badgeTone"
  | "sideLabel"
  | "pnlLabel"
  | "pnlTone"
  | "feeLabel"
  | "netPnlLabel"
> {
  const reduceOnly = Boolean(execution.reduceOnly);

  let sideLabel = execution.side ? visibleSide(execution.side) : isRealFill ? "체결" : "확인";

  if (!isRealFill) {
    const isError = ["exchange_error", "error", "rejected"].includes(execution.status);
    return {
      kind: "status",
      badgeLabel: isError ? "오류" : "상태",
      badgeTone: isError ? "error" : "neutral",
      sideLabel,
      pnlLabel: nonFillStatusLabel(execution.status),
      pnlTone: "flat",
    };
  }

  if (reduceOnly) {
    const symbol = inferredSymbol(execution.intentId, execution.symbol);
    const remainingSize = portfolioPositions?.get(symbol);
    const liveRunnerRemains =
      remainingSize !== undefined && Number.isFinite(remainingSize) && remainingSize !== 0;
    const historicalRunnerRemains = runnerRemainedByExitId?.get(execution.intentId) === true;
    const runnerRemains = liveRunnerRemains || historicalRunnerRemains;
    if (execution.side) {
      sideLabel += runnerRemains ? " (축소)" : " (청산)";
    }
    if (closedPnlNum === 0 && feesNum > 0 && !runnerRemains) {
      return {
        kind: "exit_breakeven",
        badgeLabel: "본전+수수료",
        badgeTone: "fee",
        sideLabel,
        pnlLabel: "본전",
        pnlTone: "flat",
        feeLabel: formatSignedUsdc(-feesNum),
        netPnlLabel: formatSignedUsdc(-feesNum),
      };
    }
    const exitPnl = exitPnlPresentation(closedPnlNum, feesNum);
    if (runnerRemains) {
      return {
        kind: "exit_reduce",
        badgeLabel: "축소",
        badgeTone: "close",
        sideLabel,
        pnlLabel: exitPnl.pnlLabel,
        pnlTone: exitPnl.pnlTone,
        feeLabel: feesNum > 0 ? formatSignedUsdc(-feesNum) : undefined,
        netPnlLabel: formatSignedUsdc(closedPnlNum - feesNum),
      };
    }
    return {
      kind: "exit",
      badgeLabel: "청산",
      badgeTone: "close",
      sideLabel,
      pnlLabel: exitPnl.pnlLabel,
      pnlTone: exitPnl.pnlTone,
      feeLabel: feesNum > 0 ? formatSignedUsdc(-feesNum) : undefined,
      netPnlLabel: formatSignedUsdc(closedPnlNum - feesNum),
    };
  }

  if (closedPnlNum === 0 && feesNum > 0) {
    return {
      kind: "fee_only",
      badgeLabel: "진입·수수료",
      badgeTone: "fee",
      sideLabel,
      pnlLabel:
        execution.side === "buy" ? "롱 진입" : execution.side === "sell" ? "숏 진입" : "진입",
      pnlTone: "flat",
      feeLabel: formatSignedUsdc(-feesNum),
      netPnlLabel: formatSignedUsdc(-feesNum),
    };
  }

  if (execution.side === "buy") {
    return {
      kind: "entry",
      badgeLabel: "롱",
      badgeTone: "long",
      sideLabel,
      pnlLabel: closedPnlNum === 0 ? entryDirectionLabel("buy") : formatSignedUsdc(closedPnlNum),
      pnlTone: closedPnlNum === 0 ? "flat" : toneFor(closedPnlNum),
      feeLabel: feesNum > 0 ? formatSignedUsdc(-feesNum) : undefined,
      netPnlLabel: formatSignedUsdc(closedPnlNum - feesNum),
    };
  }

  if (execution.side === "sell") {
    return {
      kind: "entry",
      badgeLabel: "숏",
      badgeTone: "short",
      sideLabel,
      pnlLabel: closedPnlNum === 0 ? entryDirectionLabel("sell") : formatSignedUsdc(closedPnlNum),
      pnlTone: closedPnlNum === 0 ? "flat" : toneFor(closedPnlNum),
      feeLabel: feesNum > 0 ? formatSignedUsdc(-feesNum) : undefined,
      netPnlLabel: formatSignedUsdc(closedPnlNum - feesNum),
    };
  }

  return {
    kind: "entry",
    badgeLabel: "체결",
    badgeTone: "neutral",
    sideLabel,
    pnlLabel:
      closedPnlNum === 0 ? entryDirectionLabel(execution.side) : formatSignedUsdc(closedPnlNum),
    pnlTone: closedPnlNum === 0 ? "flat" : toneFor(closedPnlNum),
    feeLabel: feesNum > 0 ? formatSignedUsdc(-feesNum) : undefined,
    netPnlLabel: formatSignedUsdc(closedPnlNum - feesNum),
  };
}

export function filterTradeRows(rows: TradeRow[], filter: TradeTapeFilter): TradeRow[] {
  if (filter === "all") return rows;
  if (filter === "fees") return rows.filter((row) => row.kind === "fee_only");
  if (filter === "errors") return rows.filter((row) => row.kind === "status");
  return rows.filter(
    (row) =>
      row.kind === "entry" ||
      row.kind === "exit" ||
      row.kind === "exit_reduce" ||
      row.kind === "exit_breakeven",
  );
}

export function tradePnlClassName(tone: TradeRow["pnlTone"]) {
  if (tone === "loss") return "loss-text";
  if (tone === "gain") return "gain-text";
  return "muted-text";
}

export function summarizeTradeTape(rows: TradeRow[]) {
  const feeOnly = rows.filter((row) => row.kind === "fee_only").length;
  const exitBreakeven = rows.filter((row) => row.kind === "exit_breakeven").length;
  const trades = rows.filter(
    (row) =>
      row.kind === "entry" ||
      row.kind === "exit" ||
      row.kind === "exit_reduce" ||
      row.kind === "exit_breakeven",
  ).length;
  return { feeOnly, exitBreakeven, trades, total: rows.length };
}

function executionTapeTimestamp(
  execution: LedgerSummaryResponse["recentExecutions"][number],
  ledger?: LedgerSummaryResponse,
) {
  const observedAt = executionObservedAt(execution, ledger);
  const parsed = Date.parse(String(observedAt ?? ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Ledger replay: partial reduce leaves open size; used when live portfolio is already flat. */
export function buildRunnerRemainedByExitId(
  executions: LedgerSummaryResponse["recentExecutions"],
): Map<string, boolean> {
  const result = new Map<string, boolean>();
  const openSizeBySymbol = new Map<string, number>();
  const chronological = [...executions].sort(
    (left, right) => executionTapeTimestamp(left) - executionTapeTimestamp(right),
  );

  for (const execution of chronological) {
    if (!executionHasFillEvidence(execution)) continue;
    const symbol = inferredSymbol(execution.intentId, execution.symbol);
    const fillSize = Math.abs(Number(execution.fillResult?.size ?? execution.size ?? 0));
    if (!Number.isFinite(fillSize) || fillSize <= 0) continue;

    if (execution.reduceOnly !== true) {
      openSizeBySymbol.set(symbol, (openSizeBySymbol.get(symbol) ?? 0) + fillSize);
      continue;
    }

    const openBefore = openSizeBySymbol.get(symbol) ?? 0;
    result.set(execution.intentId, openBefore > 0 && fillSize < openBefore);
    openSizeBySymbol.set(symbol, Math.max(0, openBefore - fillSize));
  }

  return result;
}

function attachRoundTripNetLabels(
  rows: TradeRow[],
  executions: LedgerSummaryResponse["recentExecutions"],
  ledger?: LedgerSummaryResponse,
  portfolioPositions?: Map<string, number>,
  runnerRemainedByExitId?: Map<string, boolean>,
): TradeRow[] {
  const roundTripByExitId = new Map<string, string>();
  const pendingEntryBySymbol = new Map<string, LedgerSummaryResponse["recentExecutions"][number]>();
  const chronological = [...executions].sort(
    (left, right) => executionTapeTimestamp(left, ledger) - executionTapeTimestamp(right, ledger),
  );

  for (const execution of chronological) {
    if (!executionHasFillEvidence(execution)) continue;
    const symbol = inferredSymbol(execution.intentId, execution.symbol);
    if (execution.reduceOnly === true) {
      const remainingSize = portfolioPositions?.get(symbol);
      const liveRunnerRemains =
        remainingSize !== undefined && Number.isFinite(remainingSize) && remainingSize !== 0;
      const historicalRunnerRemains = runnerRemainedByExitId?.get(execution.intentId) === true;
      if (liveRunnerRemains || historicalRunnerRemains) continue;
      const entry = pendingEntryBySymbol.get(symbol);
      if (!entry) continue;
      const entryFees = Number(entry.fees ?? 0);
      const exitFees = Number(execution.fees ?? 0);
      const netPnl = Number(execution.closedPnl ?? 0) - entryFees - exitFees;
      if (netPnl === 0 && entryFees === 0 && exitFees === 0) continue;
      roundTripByExitId.set(execution.intentId, formatSignedUsdc(netPnl));
      pendingEntryBySymbol.delete(symbol);
      continue;
    }
    pendingEntryBySymbol.set(symbol, execution);
  }

  return rows.map((row) => {
    const roundTripNetLabel = roundTripByExitId.get(row.id);
    if (!roundTripNetLabel || (row.kind !== "exit" && row.kind !== "exit_breakeven")) {
      return row;
    }
    return { ...row, roundTripNetLabel };
  });
}

export function buildPositionCards(
  portfolio: PortfolioResponse | undefined,
  ledger: LedgerSummaryResponse | undefined,
  now = new Date(),
): PositionCard[] {
  return (portfolio?.userState?.assetPositions ?? []).flatMap((entry) => {
    const position = entry.position;
    const symbol = position?.coin?.toUpperCase();
    if (!position || !symbol) return [];
    const size = Number(position.szi);
    if (!Number.isFinite(size) || size === 0) return [];
    const openExecution = matchOpenEntryExecution(symbol, ledger);
    const openedAt = executionObservedAt(openExecution, ledger);
    const entryReason = formatPositionEntryReason(openExecution?.thesis);
    const pnlTone = toneFor(position.unrealizedPnl);
    const liqPx = position.liquidationPx;
    return [
      {
        symbol,
        direction: !Number.isFinite(size) || size === 0 ? "flat" : size > 0 ? "long" : "short",
        imageUrl: tokenImageUrl(symbol),
        sizeLabel: formatNumber(position.szi),
        entryLabel: formatDollar(position.entryPx),
        valueLabel: formatDollar(position.positionValue),
        pnlLabel: formatSignedUsdc(position.unrealizedPnl),
        pnlTone,
        roeLabel: percentLabel(position.returnOnEquity),
        leverageLabel: position.leverage?.value ? `${position.leverage.value}x` : "-",
        marginLabel: formatDollar(position.marginUsed),
        liquidationLabel: liqPx && Number(liqPx) > 0 ? formatDollar(liqPx) : undefined,
        openedAtLabel: timeLabel(openedAt),
        elapsedLabel: elapsedLabel(openedAt, now),
        entryReason,
      },
    ];
  });
}

export function buildTradeRows(
  ledger: LedgerSummaryResponse | undefined,
  portfolio?: PortfolioResponse,
): TradeRow[] {
  const executions = ledger?.recentExecutions ?? [];
  const portfolioPositions = portfolioSizeBySymbol(portfolio);
  const runnerRemainedByExitId = buildRunnerRemainedByExitId(executions);
  const rows = executions.map((execution) => {
    const symbol = inferredSymbol(execution.intentId, execution.symbol);
    const observedAt = executionObservedAt(execution, ledger);
    const isRealFill = executionHasFillEvidence(execution);
    const closedPnlNum = Number(execution.closedPnl ?? 0);
    const feesNum = Number(execution.fees ?? 0);
    const classified = classifyTradeRow(
      execution,
      isRealFill,
      closedPnlNum,
      feesNum,
      portfolioPositions,
      runnerRemainedByExitId,
    );

    const sideMark = sideMarkFromExecution(execution, classified.kind, classified.badgeTone);

    return {
      id: execution.intentId,
      noteId: execution.noteId,
      symbol,
      imageUrl: tokenImageUrl(symbol),
      sizeLabel: formatNumber(execution.size ?? execution.fillResult?.size),
      priceLabel: formatDollar(execution.fillResult?.avgPx),
      timeLabel: timeLabel(observedAt ?? ledger?.observedAt),
      sideMark,
      ...classified,
    };
  });
  return attachRoundTripNetLabels(
    rows,
    executions,
    ledger,
    portfolioPositions,
    runnerRemainedByExitId,
  );
}
